import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { PackagePlus, Plus, Trash2, X, CheckCircle2, RefreshCw, FileText, Pencil, Search, Link2, Unlink, FileUp } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { resolveImageUrl } from "../../components/ImageUpload";
import ProductHoverPreview from "../../components/ProductHoverPreview";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CURRENCIES = ["EUR", "USD", "KZT"];

const STATUS_LABELS = { draft: "Черновик", posted: "Проведено", cancelled: "Отменено" };
const STATUS_COLORS = { draft: "#94a3b8", posted: "#16a34a", cancelled: "#dc2626" };

export default function AdminArrivals() {
  const [arrivals, setArrivals] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingArrival, setEditingArrival] = useState(null);   // full arrival object being edited
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, w] = await Promise.all([
        axios.get(`${API}/admin/arrivals?limit=200`, { withCredentials: true }),
        axios.get(`${API}/admin/warehouses`, { withCredentials: true }),
      ]);
      setArrivals(a.data || []);
      setWarehouses(w.data || []);
    } catch (e) {
      // 403 → not enough privileges
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (arrival) => {
    setSelected(null);
    setEditingArrival(arrival);
    setShowForm(true);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontFamily: "Cormorant Garamond", fontSize: 28, color: "#111", margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
            <PackagePlus size={26} color="#C5A059" /> Поступления
          </h2>
          <div style={{ fontFamily: "Manrope", fontSize: 13, color: "#64748b", marginTop: 4 }}>
            Инвойсы от фабрик, приход товара на склад с автоматическим присвоением SKU
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={load} style={btnSec}><RefreshCw size={13} /> Обновить</button>
          <button data-testid="new-arrival-btn" onClick={() => { setEditingArrival(null); setShowForm(true); }} style={btnPrim}><Plus size={14} /> Новое поступление</button>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontFamily: "Manrope" }}>Загрузка…</div>
      ) : arrivals.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontFamily: "Manrope", fontSize: 13, background: "white", borderRadius: 8, border: "1px dashed #e5e7eb" }}>
          Поступлений пока нет. Нажмите «Новое поступление» чтобы создать первое.
        </div>
      ) : (
        <div style={{ background: "white", borderRadius: 8, border: "1px solid #e5e7eb", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Manrope", fontSize: 13 }}>
            <thead style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
              <tr>
                <th style={th}>№ Инвойса</th>
                <th style={th}>Дата</th>
                <th style={th}>Бренд</th>
                <th style={th}>Валюта</th>
                <th style={th}>Курс</th>
                <th style={{ ...th, textAlign: "right" }}>Итого KZT</th>
                <th style={th}>Статус</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {arrivals.map(a => (
                <tr key={a.id} data-testid={`arrival-row-${a.id}`} style={{ borderBottom: "1px solid #f3f4f6", cursor: "pointer" }} onClick={() => setSelected(a.id)}>
                  <td style={{ ...td, fontFamily: "monospace", fontSize: 12, fontWeight: 600 }}>{a.invoice_number}</td>
                  <td style={{ ...td, color: "#64748b" }}>{a.invoice_date}</td>
                  <td style={td}>{a.supplier_brand}</td>
                  <td style={{ ...td, color: "#64748b" }}>{a.currency}</td>
                  <td style={{ ...td, color: "#64748b" }}>{a.exchange_rate_to_kzt}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{a.total_cost_kzt ? Number(a.total_cost_kzt).toLocaleString("ru-RU") + " ₸" : "—"}</td>
                  <td style={td}>
                    <span style={{ background: STATUS_COLORS[a.status], color: "white", padding: "2px 10px", borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                      {STATUS_LABELS[a.status] || a.status}
                    </span>
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>
                    <button style={btnIcon} onClick={e => { e.stopPropagation(); setSelected(a.id); }}><FileText size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && <ArrivalForm warehouses={warehouses} existing={editingArrival} onClose={saved => { setShowForm(false); setEditingArrival(null); if (saved) load(); }} />}
      {selected && <ArrivalDetail arrivalId={selected} onEdit={openEdit} onClose={reload => { setSelected(null); if (reload) load(); }} />}
    </div>
  );
}

// SkuLookup: inline product search for arrival lines.
// Behaviour:
//  - When unlinked, shows a text input; typing 2+ chars triggers /admin/products/search
//    and renders a dropdown of matches with thumbnail + our_sku + name + brand.
//  - When linked, shows a compact card (thumbnail + our_sku + product name) with
//    a "×" button to unlink.
function SkuLookup({ line, onLink, onUnlink }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);

  // Fetch suggestions with a 200ms debounce.
  useEffect(() => {
    if (line.product_id) return; // linked — skip search
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.trim().length < 2) { setResults([]); return; }
    debounceRef.current = setTimeout(() => {
      setLoading(true);
      axios.get(`${API}/admin/products/search`, {
        params: { q: q.trim(), limit: 8 },
        withCredentials: true,
      }).then(r => { setResults(r.data || []); setOpen(true); })
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 200);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q, line.product_id]);

  if (line.product_id) {
    return (
      <div data-testid="sku-linked-card" style={{ display: "flex", alignItems: "center", gap: 8, background: "#eef2ff", border: "1px solid #c7d2fe", borderRadius: 6, padding: "6px 8px" }}>
        {line.image_hint ? (
          <img src={resolveImageUrl(line.image_hint)} alt="" style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 4, background: "#0A0A0A" }} onError={e => { e.target.style.display = "none"; }} />
        ) : (
          <div style={{ width: 36, height: 36, borderRadius: 4, background: "#e5e7eb", display: "flex", alignItems: "center", justifyContent: "center" }}><Link2 size={14} color="#94a3b8" /></div>
        )}
        <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
          <div style={{ fontFamily: "monospace", fontSize: 11, fontWeight: 700, color: "#4338ca" }}>{line.our_sku_hint || "(SKU будет присвоен)"}</div>
          {line.factory_sku_hint && (
            <div data-testid="linked-factory-sku" style={{ fontFamily: "monospace", fontSize: 10, color: "#64748b", marginTop: 1 }}>
              ⇄ фабр. {line.factory_sku_hint}
            </div>
          )}
          <div style={{ fontSize: 11, color: "#111", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{line.product_name}</div>
        </div>
        <button data-testid="sku-unlink-btn" onClick={onUnlink} title="Отвязать" style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b", padding: 2 }}>
          <Unlink size={14} />
        </button>
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <Search size={12} color="#94a3b8" style={{ position: "absolute", left: 8, top: 9, pointerEvents: "none" }} />
        <input
          data-testid="sku-lookup-input"
          value={q}
          onChange={e => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => q.length >= 2 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 180)}
          placeholder="SKU или название…"
          style={{ ...inp, marginTop: 0, fontSize: 12, padding: "6px 8px 6px 26px", width: "100%" }}
        />
      </div>
      {open && q.trim().length >= 2 && (
        <div data-testid="sku-lookup-dropdown" style={{ position: "absolute", top: "100%", left: 0, right: 0, marginTop: 2, background: "white", border: "1px solid #d1d5db", borderRadius: 6, boxShadow: "0 8px 20px rgba(0,0,0,0.12)", zIndex: 100, maxHeight: 280, overflowY: "auto" }}>
          {loading && <div style={{ padding: 8, fontSize: 11, color: "#64748b" }}>Поиск…</div>}
          {!loading && results.length === 0 && <div style={{ padding: 8, fontSize: 11, color: "#64748b" }}>Ничего не найдено. Строка сохранится как новый товар (SKU будет создан при проведении).</div>}
          {!loading && results.map(p => (
            <button
              key={p.id}
              data-testid={`sku-lookup-result-${p.id}`}
              onMouseDown={e => { e.preventDefault(); onLink(p); setQ(""); setOpen(false); }}
              style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "6px 8px", background: "white", border: "none", borderBottom: "1px solid #f3f4f6", cursor: "pointer", textAlign: "left" }}
            >
              {p.image ? (
                <img src={resolveImageUrl(p.image)} alt="" style={{ width: 32, height: 32, objectFit: "cover", borderRadius: 4, background: "#0A0A0A" }} onError={e => { e.target.style.display = "none"; }} />
              ) : (
                <div style={{ width: 32, height: 32, borderRadius: 4, background: "#e5e7eb" }} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "monospace", fontSize: 10, fontWeight: 700, color: "#4338ca" }}>
                  {p.our_sku || "(SKU будет присвоен)"}
                  {(p.sku || p.barcode) && (
                    <span style={{ marginLeft: 6, color: "#64748b", fontWeight: 500 }}>⇄ {p.sku || p.barcode}</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: "#111", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</div>
                <div style={{ fontSize: 10, color: "#64748b" }}>{p.brand || "—"} · {p.category || "—"}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ArrivalForm({ warehouses, existing, onClose }) {
  const isEdit = !!existing;
  const [invoiceNumber, setInvoiceNumber] = useState(existing?.invoice_number || "");
  const [invoiceDate, setInvoiceDate] = useState(existing?.invoice_date || new Date().toISOString().slice(0, 10));
  const [supplierBrand, setSupplierBrand] = useState(existing?.supplier_brand || "Nello Santi");
  const [currency, setCurrency] = useState(existing?.currency || "EUR");
  const [exchangeRate, setExchangeRate] = useState(existing?.exchange_rate_to_kzt ?? 540);
  const [warehouseId, setWarehouseId] = useState(existing?.warehouse_id || warehouses.find(w => w.is_default)?.id || warehouses[0]?.id || "");
  const [expenses, setExpenses] = useState(
    existing?.expenses?.length
      ? existing.expenses.map(e => ({ name: e.name, amount_kzt: e.amount_kzt }))
      : [{ name: "Доставка", amount_kzt: 0 }]
  );
  const [notes, setNotes] = useState(existing?.notes || "");
  const [lines, setLines] = useState(
    existing?.lines?.length
      ? existing.lines.map(l => ({
          factory_sku: l.factory_sku || "",
          product_id: l.product_id || null,
          product_name: l.product_name || "",
          brand: l.brand || existing.supplier_brand || "Nello Santi",
          category: l.category || "meha",
          size: l.size || "",
          color: l.color || "",
          qty: l.qty || 1,
          cost_price_original: l.cost_price_original || 0,
          our_sku_hint: l.our_sku || null,   // filled when linked to catalog product
          image_hint: l.image || null,
        }))
      : [blankLine()]
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const dirtyRef = useRef(false);

  // Any user-driven mutation flips dirty. We wrap setters via a helper.
  const markDirty = () => { dirtyRef.current = true; };

  function blankLine() {
    return { factory_sku: "", product_id: null, product_name: "", brand: "Nello Santi", category: "meha", size: "", color: "", qty: 1, cost_price_original: 0, our_sku_hint: null, image_hint: null };
  }

  // Link an arrival line to a catalog product. Locks product_name/brand/category
  // fields (rendered read-only) and stores product_id so post_arrival() reuses
  // the product's `our_sku` instead of generating a new one. Also pre-fills
  // `factory_sku` from the product if the arrival line didn't have one yet —
  // this keeps the factory article in sync in BOTH directions between catalog
  // card and arrival.
  const linkLineToProduct = (idx, product) => {
    markDirty();
    setLines(p => p.map((x, j) => j === idx ? {
      ...x,
      product_id: product.id,
      product_name: product.name || x.product_name,
      brand: product.brand || x.brand,
      category: product.category || x.category,
      factory_sku: (x.factory_sku || product.sku || product.barcode || "").trim(),
      our_sku_hint: product.our_sku,
      factory_sku_hint: product.sku || product.barcode || null,
      image_hint: product.image,
    } : x));
  };

  const unlinkLine = (idx) => {
    markDirty();
    setLines(p => p.map((x, j) => j === idx ? {
      ...x, product_id: null, our_sku_hint: null, factory_sku_hint: null, image_hint: null,
    } : x));
  };

  // Invoice PDF import (LLM-based). See /app/backend/invoice_parser.py.
  const invoiceInputRef = useRef(null);
  const [parseBusy, setParseBusy] = useState(false);
  const [parsePreview, setParsePreview] = useState(null);   // {supplier, invoice_number, invoice_date, currency_hint, lines[]}
  const [parseSelected, setParseSelected] = useState({});   // {idx: bool}

  const handleInvoiceFile = async (file) => {
    if (!file) return;
    // Client-side guard mirrors the backend limit — saves bandwidth + LLM cost.
    if (file.size > 15 * 1024 * 1024) {
      setError(`Файл больше 15 МБ (${(file.size / 1024 / 1024).toFixed(1)} МБ). Сожмите PDF или уменьшите разрешение изображения.`);
      if (invoiceInputRef.current) invoiceInputRef.current.value = "";
      return;
    }
    setParseBusy(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/arrivals/parse-invoice`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const p = r.data || { lines: [] };
      setParsePreview(p);
      // Pre-select all lines
      const sel = {};
      (p.lines || []).forEach((_, i) => { sel[i] = true; });
      setParseSelected(sel);
    } catch (e) {
      setError(e?.response?.data?.detail || "Не удалось распознать инвойс");
    } finally {
      setParseBusy(false);
      // Reset input so same file can be re-uploaded
      if (invoiceInputRef.current) invoiceInputRef.current.value = "";
    }
  };

  const applyParsedLines = () => {
    if (!parsePreview) return;
    const selectedLines = (parsePreview.lines || [])
      .map((l, i) => ({ ...l, _idx: i }))
      .filter(l => parseSelected[l._idx]);
    if (selectedLines.length === 0) { setParsePreview(null); return; }

    markDirty();

    // Pre-fill supplier + currency in the header if empty & LLM suggested them
    if (parsePreview.supplier && !supplierBrand) setSupplierBrand(parsePreview.supplier);
    if (parsePreview.invoice_number && !invoiceNumber) setInvoiceNumber(parsePreview.invoice_number);
    if (parsePreview.invoice_date && (!invoiceDate || invoiceDate === new Date().toISOString().slice(0, 10))) {
      setInvoiceDate(parsePreview.invoice_date);
    }
    if (parsePreview.currency_hint && ["EUR", "USD", "KZT", "UZS", "CNY", "RUB", "GBP", "TRY"].includes(parsePreview.currency_hint)) {
      setCurrency(parsePreview.currency_hint);
    }

    const newLines = selectedLines.map(l => ({
      factory_sku: l.factory_sku || "",
      product_id: null,
      product_name: l.product_name || l.factory_sku || "",
      brand: parsePreview.supplier || supplierBrand || "Nello Santi",
      category: "meha",
      size: l.size || "",
      color: l.color || "",
      qty: l.qty || 1,
      cost_price_original: l.cost_price_original || 0,
      our_sku_hint: null,
      image_hint: null,
    }));

    // If current lines are just one empty blank, replace them; otherwise append
    const isBlankOnly = lines.length === 1 && !lines[0].product_name && !lines[0].factory_sku;
    setLines(isBlankOnly ? newLines : [...lines, ...newLines]);
    setParsePreview(null);
    setParseSelected({});
  };

  const requestClose = () => {
    if (!dirtyRef.current) { onClose(false); return; }
    if (window.confirm("Закрыть без сохранения? Введённые данные будут потеряны.")) {
      onClose(false);
    }
  };

  const submit = async () => {
    setError(""); setBusy(true);
    try {
      const payload = {
        invoice_number: invoiceNumber,
        invoice_date: invoiceDate,
        supplier_brand: supplierBrand,
        currency,
        exchange_rate_to_kzt: parseFloat(exchangeRate),
        warehouse_id: warehouseId,
        expenses: expenses.filter(e => e.name && e.amount_kzt > 0).map(e => ({ name: e.name, amount_kzt: parseFloat(e.amount_kzt) })),
        notes,
        lines: lines.filter(l => l.product_name && l.qty > 0).map(l => ({
          factory_sku: l.factory_sku || null,
          product_id: l.product_id || null,
          product_name: l.product_name,
          brand: l.brand || supplierBrand,
          category: l.category,
          size: l.size,
          color: l.color,
          qty: parseInt(l.qty, 10),
          cost_price_original: parseFloat(l.cost_price_original),
        })),
      };
      if (!payload.invoice_number || !payload.warehouse_id) throw new Error("Заполните № инвойса и склад");
      if (payload.lines.length === 0) throw new Error("Добавьте хотя бы одну строку");
      if (isEdit) {
        await axios.patch(`${API}/admin/arrivals/${existing.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/arrivals`, payload, { withCredentials: true });
      }
      dirtyRef.current = false;
      onClose(true);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Ошибка");
    } finally { setBusy(false); }
  };

  return (
    <div style={overlay}>
      <div data-testid="arrival-form" style={{ ...modalBox, maxWidth: 900 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: 22, color: "#111", margin: 0 }}>
            {isEdit ? `Редактирование черновика · ${existing.invoice_number || ""}` : "Новое поступление"}
          </h3>
          <button data-testid="arrival-close-x" onClick={requestClose} style={btnIcon}><X size={16} /></button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <label style={lbl}>№ Инвойса
            <input data-testid="arr-invoice" value={invoiceNumber} onChange={e => { markDirty(); setInvoiceNumber(e.target.value); }} placeholder="INV-2026-08" style={inp} />
          </label>
          <label style={lbl}>Дата инвойса
            <input type="date" value={invoiceDate} onChange={e => { markDirty(); setInvoiceDate(e.target.value); }} style={inp} />
          </label>
          <label style={lbl}>Бренд-поставщик
            <input data-testid="arr-brand" value={supplierBrand} onChange={e => { markDirty(); setSupplierBrand(e.target.value); }} style={inp} />
          </label>
          <label style={lbl}>Склад назначения
            <select data-testid="arr-warehouse" value={warehouseId} onChange={e => { markDirty(); setWarehouseId(e.target.value); }} style={sel}>
              {warehouses.map(w => <option key={w.id} value={w.id}>{w.name} ({w.code})</option>)}
            </select>
          </label>
          <label style={lbl}>Валюта
            <select data-testid="arr-currency" value={currency} onChange={e => { markDirty(); setCurrency(e.target.value); }} style={sel}>
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label style={lbl}>Курс к KZT {currency !== "KZT" && `(1 ${currency} = X ₸)`}
            <input type="number" step="0.01" value={exchangeRate} onFocus={e => e.target.select()} onChange={e => { markDirty(); setExchangeRate(e.target.value); }} disabled={currency === "KZT"} style={inp} />
          </label>
        </div>

        <div style={{ fontFamily: "Manrope", fontSize: 12, fontWeight: 700, color: "#374151", marginTop: 14, marginBottom: 6 }}>Расходы (транспорт, пошлина и т.п. — в ₸)</div>
        {expenses.map((e, i) => (
          <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
            <input placeholder="Название" value={e.name} onChange={ev => { markDirty(); setExpenses(prev => prev.map((x, j) => j === i ? { ...x, name: ev.target.value } : x)); }} style={{ ...inp, flex: 1 }} />
            <input type="number" placeholder="₸" value={e.amount_kzt} onFocus={ev => ev.target.select()} onChange={ev => { markDirty(); setExpenses(prev => prev.map((x, j) => j === i ? { ...x, amount_kzt: ev.target.value } : x)); }} style={{ ...inp, width: 140 }} />
            <button onClick={() => { markDirty(); setExpenses(prev => prev.filter((_, j) => j !== i)); }} style={btnIcon}><Trash2 size={14} color="#dc2626" /></button>
          </div>
        ))}
        <button onClick={() => { markDirty(); setExpenses(prev => [...prev, { name: "", amount_kzt: 0 }]); }} style={btnSec}><Plus size={12} /> Добавить расход</button>

        <div style={{ fontFamily: "Manrope", fontSize: 12, fontWeight: 700, color: "#374151", marginTop: 18, marginBottom: 6, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          Строки инвойса
          <span style={{ fontWeight: 400, color: "#64748b", fontSize: 11 }}>— привяжите к товару из каталога (тот же SKU что видит клиент)</span>
          <button
            type="button"
            data-testid="import-invoice-btn"
            onClick={() => invoiceInputRef.current?.click()}
            disabled={parseBusy}
            title="Распарсить PDF/фото инвойса от фабрики через AI"
            style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 5,
                      background: "linear-gradient(135deg,#0A0A0A,#1a1a1a)", color: "#C5A059",
                      border: "1px solid #C5A059", borderRadius: 6, padding: "6px 12px",
                      fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase",
                      cursor: parseBusy ? "wait" : "pointer" }}>
            <FileUp size={12} /> {parseBusy ? "Распознаётся…" : "Импорт из PDF"}
          </button>
          <input ref={invoiceInputRef} type="file" accept="application/pdf,image/*"
                 style={{ display: "none" }}
                 onChange={e => handleInvoiceFile(e.target.files?.[0])} />
        </div>
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 6, overflow: "visible" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "auto" }}>
            <thead style={{ background: "#f9fafb" }}>
              <tr>
                <th style={{ ...th, padding: "8px", width: 220 }}>Товар из каталога</th>
                <th style={{ ...th, padding: "8px" }}>Название / Фабричный SKU</th>
                <th style={{ ...th, padding: "8px" }}>Размер</th>
                <th style={{ ...th, padding: "8px" }}>Кол-во</th>
                <th style={{ ...th, padding: "8px" }}>Цена / ед ({currency})</th>
                <th style={{ ...th, padding: "8px" }}></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #f3f4f6", verticalAlign: "top" }}>
                  <td style={{ padding: 6 }}>
                    <SkuLookup
                      line={l}
                      onLink={(prod) => linkLineToProduct(i, prod)}
                      onUnlink={() => unlinkLine(i)}
                    />
                  </td>
                  <td style={{ padding: 6 }}>
                    <input value={l.product_name} disabled={!!l.product_id} onChange={e => { markDirty(); setLines(p => p.map((x, j) => j === i ? { ...x, product_name: e.target.value } : x)); }} placeholder="Норка..." style={{ ...inp, marginTop: 0, fontSize: 12, padding: "6px 8px", background: l.product_id ? "#f3f4f6" : "white" }} />
                    <input value={l.factory_sku} onChange={e => { markDirty(); setLines(p => p.map((x, j) => j === i ? { ...x, factory_sku: e.target.value } : x)); }} placeholder="Фабричный SKU (опционально)" style={{ ...inp, marginTop: 4, fontSize: 11, padding: "4px 6px", fontFamily: "monospace", color: "#64748b" }} />
                  </td>
                  <td style={{ padding: 6 }}><input value={l.size} onFocus={e => e.target.select()} onChange={e => { markDirty(); setLines(p => p.map((x, j) => j === i ? { ...x, size: e.target.value } : x)); }} placeholder="42" style={{ ...inp, marginTop: 0, fontSize: 12, padding: "6px 8px", width: 60 }} /></td>
                  <td style={{ padding: 6 }}><input type="number" value={l.qty} onFocus={e => e.target.select()} onChange={e => { markDirty(); setLines(p => p.map((x, j) => j === i ? { ...x, qty: e.target.value } : x)); }} style={{ ...inp, marginTop: 0, fontSize: 12, padding: "6px 8px", width: 70 }} /></td>
                  <td style={{ padding: 6 }}><input type="number" step="0.01" value={l.cost_price_original} onFocus={e => e.target.select()} onChange={e => { markDirty(); setLines(p => p.map((x, j) => j === i ? { ...x, cost_price_original: e.target.value } : x)); }} style={{ ...inp, marginTop: 0, fontSize: 12, padding: "6px 8px", width: 110 }} /></td>
                  <td style={{ padding: 6 }}><button onClick={() => { markDirty(); setLines(p => p.filter((_, j) => j !== i)); }} style={btnIcon}><Trash2 size={13} color="#dc2626" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button data-testid="add-line-btn" onClick={() => { markDirty(); setLines(p => [...p, blankLine()]); }} style={{ ...btnSec, marginTop: 8 }}><Plus size={12} /> Добавить строку</button>

        <label style={{ ...lbl, marginTop: 14 }}>Заметки
          <textarea value={notes} onChange={e => { markDirty(); setNotes(e.target.value); }} rows={2} style={{ ...inp, resize: "vertical" }} />
        </label>

        {error && <div style={errBox}>{error}</div>}

        <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "flex-end" }}>
          <button data-testid="arrival-cancel" onClick={() => onClose(false)} style={btnSec}>Отмена</button>
          <button data-testid="arrival-save" onClick={submit} disabled={busy} style={btnPrim}>{busy ? "Сохранение…" : (isEdit ? "Сохранить изменения" : "Сохранить как черновик")}</button>
        </div>
      </div>

      {parsePreview && (
        <InvoiceParsePreview
          data={parsePreview}
          selected={parseSelected}
          onToggle={(i) => setParseSelected(s => ({ ...s, [i]: !s[i] }))}
          onToggleAll={(v) => {
            const next = {};
            (parsePreview.lines || []).forEach((_, i) => { next[i] = v; });
            setParseSelected(next);
          }}
          onEditLine={(i, patch) => setParsePreview(p => ({
            ...p, lines: p.lines.map((l, j) => j === i ? { ...l, ...patch } : l),
          }))}
          onCancel={() => { setParsePreview(null); setParseSelected({}); }}
          onApply={applyParsedLines}
        />
      )}
    </div>
  );
}

// Preview modal for a parsed factory invoice. User checks lines, tweaks any
// field, then applies the selection to the parent ArrivalForm's `lines`.
function InvoiceParsePreview({ data, selected, onToggle, onToggleAll, onEditLine, onCancel, onApply }) {
  const total = (data.lines || []).length;
  const selectedCount = Object.values(selected).filter(Boolean).length;
  return (
    <div style={{ ...overlay, zIndex: 210 }}>
      <div data-testid="invoice-preview-modal" style={{ ...modalBox, maxWidth: 1100 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: 22, color: "#111", margin: 0 }}>Распознанный инвойс</h3>
            <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#64748b", marginTop: 4 }}>
              {data.supplier && <>Поставщик: <b>{data.supplier}</b> · </>}
              {data.invoice_number && <>№ {data.invoice_number} · </>}
              {data.invoice_date && <>{data.invoice_date} · </>}
              {data.currency_hint && <>Валюта: <b>{data.currency_hint}</b> · </>}
              Найдено строк: <b>{total}</b>
            </div>
          </div>
          <button onClick={onCancel} style={btnIcon}><X size={16} /></button>
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 8, fontFamily: "Manrope", fontSize: 12 }}>
          <button data-testid="parse-select-all" onClick={() => onToggleAll(true)} style={btnSec}>Отметить всё</button>
          <button data-testid="parse-select-none" onClick={() => onToggleAll(false)} style={btnSec}>Снять всё</button>
          <div style={{ marginLeft: "auto", color: "#64748b", alignSelf: "center" }}>
            Выбрано: <b>{selectedCount}</b> / {total}
          </div>
        </div>

        <div style={{ border: "1px solid #e5e7eb", borderRadius: 6, maxHeight: 460, overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead style={{ background: "#f9fafb", position: "sticky", top: 0 }}>
              <tr>
                <th style={{ ...th, padding: 6, width: 30 }}></th>
                <th style={{ ...th, padding: 6 }}>Артикул</th>
                <th style={{ ...th, padding: 6 }}>Название</th>
                <th style={{ ...th, padding: 6 }}>Цвет</th>
                <th style={{ ...th, padding: 6 }}>Размер</th>
                <th style={{ ...th, padding: 6 }}>Кол-во</th>
                <th style={{ ...th, padding: 6 }}>Цена / ед</th>
              </tr>
            </thead>
            <tbody>
              {(data.lines || []).map((l, i) => (
                <tr key={i} data-testid={`parsed-row-${i}`} style={{ borderBottom: "1px solid #f3f4f6", background: selected[i] ? "white" : "#f9fafb" }}>
                  <td style={{ padding: 6, textAlign: "center" }}>
                    <input type="checkbox" checked={!!selected[i]} onChange={() => onToggle(i)} data-testid={`parsed-check-${i}`} />
                  </td>
                  <td style={{ padding: 4 }}><input value={l.factory_sku || ""} onChange={e => onEditLine(i, { factory_sku: e.target.value })} style={{ ...inp, marginTop: 0, fontSize: 12, padding: "5px 6px", fontFamily: "monospace" }} /></td>
                  <td style={{ padding: 4 }}><input value={l.product_name || ""} onChange={e => onEditLine(i, { product_name: e.target.value })} style={{ ...inp, marginTop: 0, fontSize: 12, padding: "5px 6px" }} /></td>
                  <td style={{ padding: 4 }}><input value={l.color || ""} onChange={e => onEditLine(i, { color: e.target.value })} style={{ ...inp, marginTop: 0, fontSize: 12, padding: "5px 6px", width: 90 }} /></td>
                  <td style={{ padding: 4 }}><input value={l.size || ""} onChange={e => onEditLine(i, { size: e.target.value })} style={{ ...inp, marginTop: 0, fontSize: 12, padding: "5px 6px", width: 55 }} /></td>
                  <td style={{ padding: 4 }}><input type="number" value={l.qty || 1} onChange={e => onEditLine(i, { qty: parseInt(e.target.value, 10) || 1 })} onFocus={e => e.target.select()} style={{ ...inp, marginTop: 0, fontSize: 12, padding: "5px 6px", width: 65 }} /></td>
                  <td style={{ padding: 4 }}><input type="number" step="0.01" value={l.cost_price_original || 0} onChange={e => onEditLine(i, { cost_price_original: parseFloat(e.target.value) || 0 })} onFocus={e => e.target.select()} style={{ ...inp, marginTop: 0, fontSize: 12, padding: "5px 6px", width: 100 }} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 14, justifyContent: "flex-end" }}>
          <button onClick={onCancel} style={btnSec}>Отмена</button>
          <button data-testid="parse-apply-btn" onClick={onApply} disabled={selectedCount === 0} style={btnPrim}>
            Применить {selectedCount} стр.
          </button>
        </div>
      </div>
    </div>
  );
}

function ArrivalDetail({ arrivalId, onEdit, onClose }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = () => {
    axios.get(`${API}/admin/arrivals/${arrivalId}`, { withCredentials: true }).then(r => setData(r.data)).catch(() => {});
  };
  useEffect(load, [arrivalId]);

  const postArrival = async () => {
    if (!window.confirm("Провести поступление? После этого товары попадут на склад с автоматически присвоенными SKU. Отменить нельзя.")) return;
    setBusy(true); setError("");
    try {
      await axios.post(`${API}/admin/arrivals/${arrivalId}/post`, {}, { withCredentials: true });
      onClose(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Ошибка проведения");
    } finally { setBusy(false); }
  };

  const removeDraft = async () => {
    if (!window.confirm("Удалить черновик поступления? Действие необратимо.")) return;
    setBusy(true); setError("");
    try {
      await axios.delete(`${API}/admin/arrivals/${arrivalId}`, { withCredentials: true });
      onClose(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Ошибка удаления");
    } finally { setBusy(false); }
  };

  // Admin-only: cancel a POSTED arrival. Reverses stock movements and reduces
  // the counters. Refused by backend if goods were already sold (qty_on_hand
  // less than what was received).
  const forceDelete = async () => {
    if (!window.confirm(
      "ВНИМАНИЕ! Проведённое поступление будет удалено. Остатки на складе будут уменьшены на количество из этого поступления, движения удалятся из истории. Действие необратимо. Продолжить?"
    )) return;
    setBusy(true); setError("");
    try {
      await axios.delete(`${API}/admin/arrivals/${arrivalId}?force=true`, { withCredentials: true });
      onClose(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Ошибка удаления");
    } finally { setBusy(false); }
  };

  if (!data) return null;

  const isDraft = data.status === "draft";
  const isPosted = data.status === "posted";
  const isAdmin = user?.role === "admin";

  return (
    <div style={overlay}>
      <div data-testid="arrival-detail" style={{ ...modalBox, maxWidth: 900 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div>
            <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: 22, color: "#111", margin: 0 }}>{data.invoice_number}</h3>
            <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#64748b", marginTop: 2 }}>
              {data.invoice_date} · {data.supplier_brand} · {data.currency} @ {data.exchange_rate_to_kzt}
              <span style={{ marginLeft: 12, background: STATUS_COLORS[data.status], color: "white", padding: "2px 10px", borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{STATUS_LABELS[data.status]}</span>
            </div>
          </div>
          <button data-testid="arrival-detail-close" onClick={() => onClose(false)} style={btnIcon}><X size={16} /></button>
        </div>

        {data.total_cost_kzt && (
          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, padding: "12px 14px", marginBottom: 14, fontFamily: "Manrope", fontSize: 13, color: "#111" }}>
            Итого: <strong>{Number(data.total_cost_kzt).toLocaleString("ru-RU")} ₸</strong>
            {data.expenses_total_kzt > 0 && <span style={{ color: "#64748b" }}> (расходы {Number(data.expenses_total_kzt).toLocaleString("ru-RU")} ₸)</span>}
          </div>
        )}

        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: "Manrope", background: "white", border: "1px solid #e5e7eb", borderRadius: 6, overflow: "hidden" }}>
          <thead style={{ background: "#f9fafb" }}>
            <tr>
              <th style={{ ...th, padding: 8 }}>Внутр. SKU</th>
              <th style={{ ...th, padding: 8 }}>Фабричный</th>
              <th style={{ ...th, padding: 8 }}>Товар</th>
              <th style={{ ...th, padding: 8 }}>Размер</th>
              <th style={{ ...th, padding: 8, textAlign: "center" }}>Кол-во</th>
              <th style={{ ...th, padding: 8, textAlign: "right" }}>Цена ({data.currency})</th>
              <th style={{ ...th, padding: 8, textAlign: "right" }}>Себест. KZT</th>
            </tr>
          </thead>
          <tbody>
            {(data.lines || []).map((l, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                <td style={{ padding: 8, fontFamily: "monospace", fontWeight: 600, color: "#C5A059" }}>
                  {l.internal_sku || l.our_sku || "—"}
                  {l.product_id && <Link2 size={11} color="#4338ca" style={{ marginLeft: 6, verticalAlign: "middle" }} title="Привязано к товару из каталога" />}
                </td>
                <td style={{ padding: 8, color: "#64748b", fontFamily: "monospace", fontSize: 11 }}>{l.factory_sku || "—"}</td>
                <td style={{ padding: 8 }}>
                  {l.product_id ? (
                    <ProductHoverPreview
                      productId={l.product_id}
                      fallbackName={l.product_name}
                      fallbackSku={l.internal_sku || l.our_sku}
                    >
                      {l.product_name}
                    </ProductHoverPreview>
                  ) : (
                    l.product_name
                  )}
                </td>
                <td style={{ padding: 8, color: "#64748b" }}>{l.size || "—"}</td>
                <td style={{ padding: 8, textAlign: "center", fontWeight: 600 }}>{l.qty}</td>
                <td style={{ padding: 8, textAlign: "right", color: "#64748b" }}>{Number(l.cost_price_original).toLocaleString("ru-RU")}</td>
                <td style={{ padding: 8, textAlign: "right" }}>{l.cost_price_kzt ? Number(l.cost_price_kzt).toLocaleString("ru-RU") : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {error && <div style={errBox}>{error}</div>}

        <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <button onClick={() => onClose(false)} style={btnSec}>Закрыть</button>
          {isPosted && isAdmin && (
            <button data-testid="arrival-force-delete-btn" onClick={forceDelete} disabled={busy}
                    title="Только для администратора: удалить проведённое поступление и откатить остатки"
                    style={{ ...btnSec, color: "white", background: "#dc2626", borderColor: "#dc2626" }}>
              <Trash2 size={14} /> {busy ? "Удаление…" : "Удалить проведённое (админ)"}
            </button>
          )}
          {isDraft && (
            <>
              <button data-testid="arrival-delete-btn" onClick={removeDraft} disabled={busy} style={{ ...btnSec, color: "#dc2626", borderColor: "#fecaca" }}>
                <Trash2 size={14} /> Удалить черновик
              </button>
              <button data-testid="arrival-edit-btn" onClick={() => onEdit && onEdit(data)} disabled={busy} style={btnSec}>
                <Pencil size={14} /> Редактировать
              </button>
              <button data-testid="arrival-post-btn" onClick={postArrival} disabled={busy} style={{ ...btnPrim, background: "linear-gradient(135deg,#16a34a,#22c55e)", color: "white" }}>
                <CheckCircle2 size={14} /> {busy ? "Проведение…" : "Провести (→ склад)"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const th = { textAlign: "left", padding: "10px 14px", color: "#374151", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 };
const td = { padding: "12px 14px", color: "#111" };
const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 };
const modalBox = { background: "white", borderRadius: 10, padding: 24, width: "100%", maxHeight: "92vh", overflowY: "auto", fontFamily: "Manrope" };
const lbl = { display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 12 };
const inp = { width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 12px", fontSize: 13, marginTop: 6, outline: "none" };
const sel = { ...inp, cursor: "pointer", background: "white" };
const btnPrim = { background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A", border: "none", borderRadius: 6, padding: "9px 18px", cursor: "pointer", fontFamily: "Manrope", fontWeight: 700, fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 };
const btnSec = { background: "white", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 14px", cursor: "pointer", fontFamily: "Manrope", fontWeight: 600, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 };
const btnIcon = { background: "transparent", border: "none", cursor: "pointer", color: "#64748b", padding: 4, display: "inline-flex", alignItems: "center" };
const errBox = { background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", padding: "8px 12px", borderRadius: 6, marginTop: 10, fontSize: 13 };
